export interface EventLoggingConfig {
  isInWebview?: boolean;
  isInIOS?: boolean;
  isInLocal?: boolean;
  lambdaApiUrl?: string;
}

export type Value = boolean | number | string | null | undefined;

export type Payload = Record<string, string | Value>;

let _isInWebview = false;
let _isInIOS = false;
let _isInLocal = false;
let _lambdaApiUrl = '';

// Función para obtener información de pantalla completa
const getScreenInfo = () => {
  if (typeof window === 'undefined') return {};

  return {
    density: window.devicePixelRatio || 1,
    width: window.screen.width,
    height: window.screen.height,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
  };
};

// Función para gestión de sesiones (compatible con RudderStack SDK)
const getSessionInfo = () => {
  if (typeof window === 'undefined')
    return { sessionId: Date.now(), sessionStart: false };

  const sessionKey = 'innovamat_session_id';
  const sessionStartKey = 'innovamat_session_start';

  let sessionId = localStorage.getItem(sessionKey);
  let sessionStart = false;

  if (!sessionId) {
    sessionId = Date.now().toString();
    sessionStart = true;
    localStorage.setItem(sessionKey, sessionId);
    localStorage.setItem(sessionStartKey, 'true');
  } else {
    sessionStart = localStorage.getItem(sessionStartKey) === 'true';
    localStorage.setItem(sessionStartKey, 'false');
  }

  return {
    sessionId: parseInt(sessionId),
    sessionStart,
  };
};

// Función para obtener información completa de referrer (como RudderStack SDK)
const getReferrerInfo = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return {
      referrer: '$direct',
      referring_domain: '',
      search: '',
      initial_referrer: '$direct',
      initial_referring_domain: '',
    };
  }

  const referrer = document.referrer;
  const referring_domain = referrer ? new URL(referrer).hostname : '';

  // Guardar initial referrer la primera vez (como hace RudderStack)
  if (!sessionStorage.getItem('initial_referrer')) {
    sessionStorage.setItem('initial_referrer', referrer || '$direct');
    sessionStorage.setItem('initial_referring_domain', referring_domain || '');
  }

  return {
    referrer: referrer || '$direct',
    referring_domain: referring_domain || '',
    search: window.location.search,
    initial_referrer: sessionStorage.getItem('initial_referrer') || '$direct',
    initial_referring_domain:
      sessionStorage.getItem('initial_referring_domain') || '',
  };
};

// Función para extraer parámetros de campaña (UTM, etc.)
const getCampaignInfo = () => {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);
  const campaign: Record<string, string> = {};

  // Parámetros UTM estándar
  const utmParams = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ];
  utmParams.forEach((param) => {
    const value = urlParams.get(param);
    if (value) campaign[param] = value;
  });

  // Otros parámetros de campaña comunes
  const otherParams = ['gclid', 'fbclid', 'ref', 'source'];
  otherParams.forEach((param) => {
    const value = urlParams.get(param);
    if (value) campaign[param] = value;
  });

  return campaign;
};

const sendEventToLambda = async (
  eventType: string,
  payload: Payload
): Promise<boolean> => {
  try {
    if (!_lambdaApiUrl) {
      console.error(
        'Lambda API URL not configured. Analytics events cannot be sent.'
      );
      return false;
    }

    // Obtener información adicional del navegador (como RudderStack SDK)
    const screenInfo = getScreenInfo();
    const sessionInfo = getSessionInfo();
    const referrerInfo = getReferrerInfo();
    const campaignInfo = getCampaignInfo();

    const eventPayload = {
      type: 'track',
      event: eventType,
      properties: payload,
      timestamp: new Date().toISOString(),
      context: {
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        screen: screenInfo,
        ...sessionInfo,
        campaign: campaignInfo,
        page: {
          url: typeof window !== 'undefined' ? window.location.href : '',
          path: typeof window !== 'undefined' ? window.location.pathname : '',
          title: typeof document !== 'undefined' ? document.title : '',
          tab_url: typeof window !== 'undefined' ? window.location.href : '',
          ...referrerInfo,
        },
      },
    };

    console.log('Sending enhanced event to Lambda:', {
      event: eventType,
      hasScreen: !!screenInfo.width,
      hasSession: !!sessionInfo.sessionId,
      hasReferrer: referrerInfo.referrer !== '$direct',
      hasCampaign: Object.keys(campaignInfo).length > 0,
    });

    const response = await fetch(`${_lambdaApiUrl}/analytics/eventlogging`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventPayload),
    });

    if (!response.ok) {
      console.error(`Lambda API error: ${response.status}`);
      return false;
    }

    const result = await response.json();
    if (!result.success) {
      console.error(
        'Lambda API returned error:',
        result.error || result.results
      );
      return false;
    }

    // Log información sobre qué servicios recibieron el evento
    if (result.services) {
      console.log('Event sent via Lambda API:', {
        event: eventType,
        rudderstack: result.services.rudderstack?.sent ? 'sent' : 'failed',
        iterable: result.services.iterable?.sent ? 'sent' : 'failed',
        payload_enhanced: true,
      });
    }

    return true;
  } catch (error) {
    console.error('Error sending event to Lambda API:', error);
    return false;
  }
};

const healthCheck = async () => {
  try {
    if (!_lambdaApiUrl) {
      return { status: 'not-configured', error: 'Lambda API URL not set' };
    }

    const response = await fetch(`${_lambdaApiUrl}/health`);

    if (!response.ok) {
      return {
        status: 'unhealthy',
        error: `HTTP ${response.status}`,
        lambdaApiUrl: _lambdaApiUrl,
      };
    }

    const result = await response.json();
    return {
      status: 'healthy',
      ...result,
      lambdaApiUrl: _lambdaApiUrl,
    };
  } catch (error) {
    return {
      status: 'error',
      error: error instanceof Error ? error.message : 'Unknown error',
      lambdaApiUrl: _lambdaApiUrl,
    };
  }
};

export const eventLogging = {
  init: ({
    isInWebview,
    isInIOS,
    isInLocal,
    lambdaApiUrl,
  }: EventLoggingConfig) => {
    _isInWebview = isInWebview || false;
    _isInIOS = isInIOS || false;
    _isInLocal = isInLocal || false;

    _lambdaApiUrl = lambdaApiUrl ?? '';

    console.log('EventLogging init (Enhanced Lambda API):', {
      lambdaApiUrl: _lambdaApiUrl,
      isLocal: _isInLocal,
      isWebview: _isInWebview,
      isIOS: _isInIOS,
      screenInfo: getScreenInfo(),
      sessionInfo: getSessionInfo(),
    });

    if (!_lambdaApiUrl) {
      console.warn(
        'Lambda API URL not configured. Analytics events will not work.'
      );
    } else {
      console.log(
        'Using Enhanced Lambda API for all analytics services (RudderStack + Iterable) with full SDK compatibility'
      );
    }
  },

  setDevice: (deviceId: string) => {
    // Emmagatzemar deviceId per enviar-lo amb els esdeveniments
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('innovamat_device_id', deviceId);
    }
    console.log('Device ID stored (enhanced):', deviceId);
  },

  setIterableEmail: (email: string) => {
    // Emmagatzemar email d'Iterable per enviar-lo amb els esdeveniments
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('innovamat_iterable_email', email);
    }
    console.log('Iterable email stored (enhanced):', email);
  },

  log: async (eventType: string, payload: Payload) => {
    // No enviar esdeveniments en local (comportament original)
    if (_isInLocal) {
      console.log(
        'EventLogging: Skipping event in local environment:',
        eventType
      );
      return;
    }

    if (_isInWebview) {
      payload['os'] = _isInIOS ? 'ios' : 'android';
    }

    // Afegir deviceId i email d'Iterable emmagatzemats localment
    if (typeof localStorage !== 'undefined') {
      const deviceId = localStorage.getItem('innovamat_device_id');
      const iterableEmail = localStorage.getItem('innovamat_iterable_email');

      if (deviceId) {
        payload['device_id'] = deviceId;
      }
      if (iterableEmail) {
        payload['iterable_email'] = iterableEmail;
      }
    }

    const success = await sendEventToLambda(eventType, payload);

    if (success) {
      console.log('Enhanced event sent via Lambda API:', eventType);
    } else {
      console.error('Failed to send enhanced event via Lambda API:', eventType);
    }
  },

  clear: () => {
    // Netejar informació local emmagatzemada
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('innovamat_session_id');
      localStorage.removeItem('innovamat_session_start');
      localStorage.removeItem('innovamat_device_id');
      localStorage.removeItem('innovamat_iterable_email');
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('initial_referrer');
      sessionStorage.removeItem('initial_referring_domain');
    }
    console.log('Clear event logging data (enhanced)');
  },

  healthCheck,
};
